import {
  DownloadOutlined,
  FilterList,
  DescriptionOutlined,
  Business,
  SearchOutlined,
} from '@mui/icons-material';
import { Button, CardContainer, DataTable, Dropdown } from 'finara-components';
import type { DataTableColumn } from 'finara-components';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';

import { Navbar as AdminNavbar } from '@/components/admin/general/Navbar';
import { CsvExportButton } from '@/components/common';
import { Input } from '@/components/common/Input';
import { Navbar as SuperAdminNavbar } from '@/components/super-admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { useAccountStatements } from '@/hooks/queries/useAccountStatements';
import { useAuth } from '@/hooks/useAuth';
import { accountStatementsService } from '@/services/accountStatements.service';
import type { AccountStatement, AccountStatementFilters } from '@/types/accountStatements';
import { formatCurrency } from '@/utils/formatters';

const RELEASE_YEAR = 2026;

export const AccountStatementsPage: React.FC = () => {
  const { user } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isSuperAdmin = user?.role?.toLowerCase() === 'super_admin';

  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState<AccountStatementFilters>({
    page: 1,
    limit: 10,
    orderDirection: 'DESC',
    year: currentYear,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Fetch account statements
  const { data: statementsData, isLoading, error } = useAccountStatements(filters);

  // Handle filter changes
  const handleFilterChange = (field: keyof AccountStatementFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
      page: 1, // Reset to first page when filters change
    }));
  };

  // Handle search
  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm || undefined,
      page: 1,
    }));
  };

  // Handle PDF download
  const handleDownload = async (statement: AccountStatement) => {
    setDownloadingId(statement.id);
    try {
      const { signedUrl } = await accountStatementsService.getSignedUrl(statement.id);
      window.open(signedUrl, '_blank');
      toast.success('Estado de cuenta descargado');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al descargar el estado de cuenta';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  // Format period display
  const formatPeriod = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })} - ${endDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;
  };

  // Year options
  const yearOptions = useMemo(() => {
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      if (i >= RELEASE_YEAR) {
        years.push({ value: i.toString(), label: i.toString() });
      }
    }
    return [{ value: '', label: 'Todos los años' }, ...years];
  }, [currentYear]);

  // Month options
  const monthOptions = [
    { value: '', label: 'Todos los meses' },
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  // Define columns for DataTable
  const baseColumns: DataTableColumn<AccountStatement>[] = [
    {
      key: 'fileName',
      label: 'Archivo',
      render: (_, statement) => (
        <div className="flex items-center gap-2">
          <DescriptionOutlined className="text-muted" fontSize="small" />
          <div>
            <div className="font-medium text-brand-white">{statement.fileName}</div>
            <div className="text-sm text-muted">{formatPeriod(statement.periodStart, statement.periodEnd)}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'openingBalance',
      label: 'Saldo Inicial',
      render: (_, statement) => (
        <div className="font-medium text-brand-white">{formatCurrency(statement.openingBalance)}</div>
      ),
    },
    {
      key: 'closingBalance',
      label: 'Saldo Final',
      render: (_, statement) => (
        <div className="font-medium text-brand-white">{formatCurrency(statement.closingBalance)}</div>
      ),
    },
    {
      key: 'transactions',
      label: 'Transacciones',
      align: 'center',
      render: (_, statement) => (
        <div>
          <div className="font-medium text-brand-white">{statement.transactionCount}</div>
          <div className="text-xs text-muted">
            +{formatCurrency(statement.totalCredits)} / -{formatCurrency(statement.totalDebits)}
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Fecha de Creación',
      render: (_, statement) => (
        <div className="text-sm text-brand-white">
          {new Date(statement.createdAt).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, statement) => (
        <Button
          onClick={() => void handleDownload(statement)}
          startIcon={<DownloadOutlined />}
          variant="white"
          size="small"
          disabled={downloadingId === statement.id}
        >
          {downloadingId === statement.id ? 'Descargando...' : 'Descargar'}
        </Button>
      ),
    },
  ];

  if (error) {
    return (
      <div className="min-h-screen">
        {isSuperAdmin ? (
          <SuperAdminNavbar
            navItems={superAdminNavItems}
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            userName={user?.name || 'Usuario'}
            activeItem="Operaciones"
          />
        ) : (
          <AdminNavbar
            navItems={adminNavItems}
            userName={user?.name || 'Usuario'}
            pageTitle="Estados de Cuenta"
          />
        )}
        <div className="px-4 py-8">
          <CardContainer className="p-8 text-center">
            <p className="text-red-600">
              Error al cargar los estados de cuenta. Vuelva a intentarlo en unos minutos.
            </p>
          </CardContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      {isSuperAdmin ? (
        <SuperAdminNavbar
          navItems={superAdminNavItems}
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          userName={user?.name || 'Usuario'}
          activeItem="Operaciones"
        />
      ) : (
        <AdminNavbar
          navItems={adminNavItems}
          userName={user?.name || 'Usuario'}
          pageTitle="Estados de Cuenta"
        />
      )}

      {/* Main Content */}
      <div className="px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-brand-white">Estados de Cuenta</h1>
              <p className="text-muted mt-1">
                Consulta y descarga los estados de cuenta de tus operaciones
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <DescriptionOutlined className="text-brand-blue mt-0.5" fontSize="small" />
            <div>
              <p className="text-sm text-brand-white">
                <span className="font-semibold">Nota:</span> Los estados de cuenta se generan
                automáticamente a las 11:59 PM (hora de México) el último día de cada mes.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <CardContainer className="mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <FilterList className="text-muted" />
            <span className="font-medium text-brand-white">Filtros:</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Dropdown
              value={filters.year?.toString() ?? ''}
              onChange={value => handleFilterChange('year', value ? Number(value) : '')}
              options={yearOptions}
              placeholder="Año"
              className="w-full"
            />
            <Dropdown
              value={filters.month?.toString() ?? ''}
              onChange={value => handleFilterChange('month', value ? Number(value) : '')}
              options={monthOptions}
              placeholder="Mes"
              className="w-full"
            />
            <div className="lg:col-span-2">
              <div className="flex gap-2">
                <Input
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSearch()}
                  placeholder="Buscar por archivo, usuario o email..."
                  className="flex-1"
                />
                <Button onClick={handleSearch} startIcon={<SearchOutlined />} variant="black">
                  Buscar
                </Button>
              </div>
            </div>
          </div>
        </CardContainer>

        {/* Summary Cards */}
        {statementsData && statementsData.data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <CardContainer>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted">Total de Estados</p>
                  <p className="text-2xl font-bold text-brand-white">{statementsData.total}</p>
                </div>
                <Business className="text-muted" style={{ fontSize: '2.5rem' }} />
              </div>
            </CardContainer>
            <CardContainer>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted">Total Transacciones</p>
                  <p className="text-2xl font-bold text-brand-white">
                    {statementsData.data.reduce((sum, stmt) => sum + stmt.transactionCount, 0)}
                  </p>
                </div>
                <DescriptionOutlined className="text-muted" style={{ fontSize: '2.5rem' }} />
              </div>
            </CardContainer>
            <CardContainer>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted">Saldo Final Promedio</p>
                  <p className="text-2xl font-bold text-brand-white">
                    {formatCurrency(
                      statementsData.data.reduce((sum, stmt) => sum + stmt.closingBalance, 0) /
                        statementsData.data.length
                    )}
                  </p>
                </div>
                <DownloadOutlined className="text-muted" style={{ fontSize: '2.5rem' }} />
              </div>
            </CardContainer>
          </div>
        )}

        {/* Table */}
        <CardContainer>
          <div className="p-6 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-semibold text-brand-white">
              Estados de Cuenta ({statementsData?.total ?? 0})
            </h3>
            <CsvExportButton
              data={statementsData?.data ?? []}
              filename="estados_de_cuenta"
              columns={statement => ({
                Archivo: statement.fileName,
                Periodo: formatPeriod(statement.periodStart, statement.periodEnd),
                'Saldo Inicial': formatCurrency(statement.openingBalance).replace(/\s/g, ''),
                'Saldo Final': formatCurrency(statement.closingBalance).replace(/\s/g, ''),
                'Total Créditos': formatCurrency(statement.totalCredits).replace(/\s/g, ''),
                'Total Débitos': formatCurrency(statement.totalDebits).replace(/\s/g, ''),
                Transacciones: statement.transactionCount,
                'Fecha de Creación': new Date(statement.createdAt).toLocaleDateString('es-MX'),
              })}
              variant="white"
              size="small"
              buttonText="Exportar"
            />
          </div>
          <div className="p-6">
            <DataTable
              columns={baseColumns}
              data={statementsData?.data ?? []}
              loading={isLoading}
              responsive
              emptyMessage={
                <div className="py-8 text-center">
                  <DescriptionOutlined
                    className="text-muted mx-auto mb-4"
                    style={{ fontSize: '3rem' }}
                  />
                  <h3 className="text-lg font-medium text-brand-white mb-2">
                    No se encontraron estados de cuenta
                  </h3>
                  <p className="text-muted">Ajusta los filtros para ver más resultados.</p>
                </div>
              }
            />
          </div>

          {/* Pagination */}
          {statementsData && statementsData.totalPages > 1 && (
            <div className="p-6 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted">
                  Mostrando {(filters.page! - 1) * filters.limit! + 1} -{' '}
                  {Math.min(filters.page! * filters.limit!, statementsData.total)} de{' '}
                  {statementsData.total} resultados
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleFilterChange('page', (filters.page ?? 1) - 1)}
                    disabled={filters.page === 1}
                    variant="white"
                    size="small"
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-muted">
                    Página {filters.page} de {statementsData.totalPages}
                  </span>
                  <Button
                    onClick={() => handleFilterChange('page', (filters.page ?? 1) + 1)}
                    disabled={filters.page === statementsData.totalPages}
                    variant="white"
                    size="small"
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContainer>
      </div>
    </div>
  );
};
